import { gql, useQuery, useLazyQuery } from '@apollo/client'
import {
  IProductPage,
  IProductCurriculumPage,
  IProductCoursePreview,
  IProductVideoCover,
} from '@dtos/ProductPage'

export const GET_PRODUCT_PAGE = gql`
  query ProductPage($schoolUrl: String!, $slug: String!) {
    productPage(schoolUrl: $schoolUrl, slug: $slug) {
      id
      title
      subtitle
      description
      slug
      kind
      productUrl
      logo
      logoVertical
      metaTitle
      metaDescription
      certification
      availableTimeType
      availableTimeLength
      availableTimeUnit
      expireDate
      supportedCardBrands
      sparkPayInstallments {
        value
        amount
        totalWithFee
      }
      currentPaymentOption {
        id
        kind
        period
        price
        basePrice
        default
        installmentsAmountLimit
        acceptedPaymentMethods
        checkoutUrl
        facebookPixel
        gaTrackingId
        googleAdsTrackingId
        googleAdsTrackingLabel
      }
      paymentOptions {
        id
        kind
        period
        price
        basePrice
        default
        checkoutUrl
      }
      topics {
        id
        title
        description
      }
      teachers {
        id
        fullName
        photoUrl
        about
      }
      videoCover {
        id
        host
        hostKey
        thumbnail
      }
      course {
        id
        kind
        contentsCount
        curriculum(page: 1, perPage: 10) {
          totalPages
          currentPage
          nodes {
            id
            name
            subModules {
              id
              name
              lessons {
                id
                title
                mediaType
              }
            }
            lessons {
              id
              title
              mediaType
              releaseAt
              lockAt
            }
          }
        }
      }
      path {
        courses(page: 1, perPage: 10) {
          totalPages
          currentPage
          nodes {
            id
            name
            imageUrl
            previewPath
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_CURRICULUM = gql`
  query ProductPageCurriculum(
    $schoolUrl: String!
    $slug: String!
    $page: Int!
  ) {
    productPage(schoolUrl: $schoolUrl, slug: $slug) {
      id
      course {
        id
        curriculum(page: $page, perPage: 10) {
          totalPages
          currentPage
          nodes {
            id
            name
            subModules {
              id
              name
              lessons {
                id
                title
                mediaType
              }
            }
            lessons {
              id
              title
              mediaType
              releaseAt
              lockAt
            }
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_COURSE_PREVIEW = gql`
  query ProductCoursePreview(
    $schoolUrl: String!
    $slug: String!
    $courseId: ID!
  ) {
    productPage(schoolUrl: $schoolUrl, slug: $slug) {
      id
      videoCover {
        id
        host
        hostKey
        thumbnail
      }
      coursePreview(courseId: $courseId) {
        id
        name
        kind
        imageUrl
        subtitle
        description
        curriculum(page: 1, perPage: 10) {
          totalPages
          currentPage
          nodes {
            id
            name
            subModules {
              id
              name
              lessons {
                id
                title
                mediaType
              }
            }
            lessons {
              id
              title
              mediaType
              releaseAt
              lockAt
            }
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_COURSE_PREVIEW_CURRICULUM = gql`
  query ProductCoursePreviewCurriculum(
    $schoolUrl: String!
    $slug: String!
    $courseId: ID!
    $page: Int!
  ) {
    productPage(schoolUrl: $schoolUrl, slug: $slug) {
      id
      coursePreview(courseId: $courseId) {
        id
        curriculum(page: $page, perPage: 10) {
          totalPages
          currentPage
          nodes {
            id
            name
            subModules {
              id
              name
              lessons {
                id
                title
                mediaType
              }
            }
            lessons {
              id
              title
              mediaType
              releaseAt
              lockAt
            }
          }
        }
      }
    }
  }
`

export interface IProductPageResult {
  productPage: IProductPage | null
}

interface IProductCurriculumResult {
  productPage: {
    id: string
    course: { id: string; curriculum: IProductCurriculumPage } | null
  } | null
}

export const useGetProductPage = (schoolUrl: string, slug: string) => {
  const { data, loading, error } = useQuery<IProductPageResult>(
    GET_PRODUCT_PAGE,
    {
      variables: { schoolUrl, slug },
      skip: !schoolUrl || !slug,
    },
  )

  return { data: data?.productPage ?? null, loading, error }
}

export const useGetProductCurriculum = () => {
  const [getProductCurriculum, { loading }] =
    useLazyQuery<IProductCurriculumResult>(GET_PRODUCT_CURRICULUM, {
      fetchPolicy: 'no-cache',
    })

  return { getProductCurriculum, loading }
}

interface IProductCoursePreviewResult {
  productPage: {
    id: string
    videoCover: IProductVideoCover | null
    coursePreview: IProductCoursePreview | null
  } | null
}

interface IProductCoursePreviewCurriculumResult {
  productPage: {
    id: string
    coursePreview: { id: string; curriculum: IProductCurriculumPage } | null
  } | null
}

export const useGetProductCoursePreview = (
  schoolUrl: string,
  slug: string,
  courseId: string,
) => {
  const { data, loading, error } = useQuery<IProductCoursePreviewResult>(
    GET_PRODUCT_COURSE_PREVIEW,
    {
      variables: { schoolUrl, slug, courseId },
      skip: !schoolUrl || !slug || !courseId,
    },
  )

  return { data: data?.productPage ?? null, loading, error }
}

export const useGetProductCoursePreviewCurriculum = () => {
  const [getPreviewCurriculum, { loading }] =
    useLazyQuery<IProductCoursePreviewCurriculumResult>(
      GET_PRODUCT_COURSE_PREVIEW_CURRICULUM,
      { fetchPolicy: 'no-cache' },
    )

  return { getPreviewCurriculum, loading }
}
